import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, has } from 'lodash';
import Form, { FormAction, FormRefType } from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { UserRole } from '#/common/types/userTypes';
import useSession from '#/common/hooks/useSession';
import type { Location } from '#/graphql/types';
import useLocationUpdate from '../../hooks/useUpdateLocationMutation';
import useLocationCreate from '../../hooks/useCreateLocationMutation';
import useLocationDelete from '../../hooks/useDeleteLocationMutation';
import { createFields, updateFields } from './fields';
import { inUseStatuses } from '../../utils/constants';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import dxForm, { FieldDataChangedEvent } from 'devextreme/ui/form';
import { InitializedEventInfo } from 'devextreme/events';
import { setEditorOptions, setItemOptions } from '#/common/components/Form/formItemOption';

export interface LocationFormProps {
  location: Location | {};
  title: string;
}

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: false,
  [UserRole.trane_admin]: false,
  [UserRole.technician]: true,
};

const LocationForm: React.FC<LocationFormProps> = ({ location, title }) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const { user } = useSession();
  const role = user?.role as UserRole;

  const id = get(location, 'id', undefined);
  const fields = id ? updateFields : createFields;

  const { updateLocation } = useLocationUpdate();
  const { createLocation } = useLocationCreate();
  const { deleteLocation } = useLocationDelete();

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteLocation(id);
    },
    [id, deleteLocation],
  );

  const submit = useCallback(
    (data: Location) => {
      if (!id) {
        return createLocation(data);
      }
      return updateLocation(id, data);
    },
    [id, updateLocation, createLocation],
  );

  const isFormReadonly =
    formReadonlyRole[role] ||
    (has(location, 'thirdPartyId') &&
      has(location, 'source') &&
      !!(location.thirdPartyId && location.source));

  const actions = useMemo(() => {
    const result: FormAction<Location>[] = [];

    if (
      !isFormReadonly &&
      get(location, 'inUseStatus') === inUseStatuses.newlyCreated.key &&
      deleteItem
    ) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          if (!e.record?.id) {
            return;
          }

          const sure = await confirm(
            'error',
            `Are you sure you want to delete location: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteItem(e.record.id);
          e.setIsLoading(false);

          navigate('/assets/locations');
        },
      });
    }
    if (!isFormReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          if (
            e.record.inUseStatus === inUseStatuses.inUse.key &&
            has(location, 'inUseStatus') &&
            location.inUseStatus !== inUseStatuses.inUse.key
          ) {
            const sure = await confirm(
              'warning',
              'Once set to In-use, this Location cannot be deleted. Do you want to proceed?',
            );
            if (!sure) {
              return;
            }
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\\n`));
              return;
            }
            if (!id && result && result.data?.id) {
              navigate(`/assets/locations/${result.data?.id}`, { replace: true });
            }

            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, location, deleteItem]);

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;

    if (!form) return;

    const formData = form.option('formData');
    if (formData?.inUseStatus !== inUseStatuses.newlyCreated.key) {
      const inUseStatusOptions = {
        dataSource: Object.values({
          ...inUseStatuses,
          newlyCreated: { ...inUseStatuses.newlyCreated, disabled: true },
        }),
      };
      setEditorOptions(form, 'inUseStatus', inUseStatusOptions);
    }

    if (!formData?.level || formData.level === 'country') {
      setItemOptions(form, 'parentId', 'isRequired', false);
      setEditorOptions(form, 'parentId', {
        placeholder: formData?.level === 'country' ? 'Not Applicable' : 'Select a Type first',
        disabled: true,
      });
    } else {
      setItemOptions(form, 'parentId', 'isRequired', true);
      setEditorOptions(form, 'parentId', {
        disabled: false,
        placeholder: 'Search and select...',
      });
    }
  }, []);

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const { component: form, dataField: field, value } = e;
    let isRequired = true;
    let parentOptions: any;
    if (field === 'level') {
      form.updateData('parentId', null);
      if (!value || value === 'country') {
        isRequired = false;
        parentOptions = {
          disabled: true,
          placeholder: value === 'country' ? 'Not Applicable' : 'Select a Type first',
        };
      } else {
        parentOptions = {
          disabled: false,
          placeholder: 'Search and select...',
        };
      }
      setItemOptions(form, 'parentId', 'isRequired', isRequired);
      setEditorOptions(form, 'parentId', parentOptions);
    }
  }, []);

  return (
    <>
      <Form
        ref={formRef}
        supportTenant
        readonly={isFormReadonly}
        record={location as Location}
        fields={fields}
        actions={actions}
        onContentReady={onContentReady}
        onFieldDataChanged={onFieldDataChanged}
      />
    </>
  );
};

export default React.memo(LocationForm);
